import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as mentionService from '../../services/mention.service';
import { stripMentionMarkers } from '../../utils/mentions';

/**
 * "You've been mentioned" — a line at the bottom of "What's on tap for
 * today," Facebook-style: shows a count, expands to a list of everywhere
 * you're tagged, click one to jump straight there. In-app only, no email,
 * per Jenn's spec (2026-08-27).
 */
export default function MentionsBar() {
  const navigate = useNavigate();
  const [mentions, setMentions] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try { setMentions((await mentionService.getUnreadMentions()).mentions); } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    load();
    // Only fetched once on mount before — a Dashboard left open across a new
    // mention (or the tab left in the background and brought back) never
    // knew anything new arrived. Poll periodically, and also refresh the
    // moment the tab becomes visible/focused again.
    const interval = setInterval(load, 60000);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', load);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  if (mentions.length === 0) return null;

  const openMention = async (m) => {
    try { await mentionService.markMentionRead(m.id); } catch { /* non-critical */ }
    setMentions(prev => prev.filter(x => x.id !== m.id));
    if (m.context_url) navigate(m.context_url);
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <button
        onClick={() => setExpanded(v => !v)}
        className="flex items-center gap-2 text-sm font-medium text-reset-terracotta hover:underline"
      >
        <span className="w-2 h-2 rounded-full bg-reset-terracotta" />
        {mentions.every(m => m.content_type === 'new_member' || m.content_type === 'member_back')
          ? `New and returning members (${mentions.length})`
          : mentions.some(m => m.content_type === 'new_member' || m.content_type === 'member_back')
            ? `Mentions and new members (${mentions.length})`
            : `You've been mentioned (${mentions.length})`}
      </button>
      {expanded && (
        <div className="mt-2 space-y-1.5">
          {mentions.map(m => (
            <button
              key={m.id}
              onClick={() => openMention(m)}
              className="w-full text-left bg-reset-cream rounded-lg px-3 py-2 text-xs hover:bg-reset-creamDark transition-colors"
            >
              {m.content_type === 'new_member' ? (
                // Team365 only (2026-10-27): a new member just joined.
                <span className="text-reset-brown">🎉 New member: <span className="font-medium">{m.mentioning_member_name || 'Someone'}</span> just joined</span>
              ) : m.content_type === 'member_back' ? (
                <span className="text-reset-brown">👋 Welcome back: <span className="font-medium">{m.mentioning_member_name || 'Someone'}</span> rejoined</span>
              ) : (
                <>
                  <span className="font-medium text-reset-brown">{m.mentioning_member_name || 'Someone'}</span>
                  <span className="text-gray-500"> tagged you — "{stripMentionMarkers(m.context_preview) || '(no preview)'}"</span>
                </>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
